import { app } from "api";
import { useMutation, useQuery, useQueryCache } from "react-query";
import Discount, { getDiscountValues } from "classes/discount";
import firebase from "firebase/app";

export const fieldValue = firebase.firestore.FieldValue;

export function getLabRef(labId: string) {
  return app.firestore().collection("labs").doc(labId);
}

async function getLabCategory(categoryId: string) {
  const category = (
    await app.firestore().collection("testCategories").doc(categoryId).get()
  ).data();

  if (category) {
    const tests = await Promise.all(
      category?.tests?.map((testId: string) =>
        app.firestore().collection("tests").doc(testId).get()
      )
    );

    category.tests = tests.map((test: any) => test.data());
  }
  return category;
}

async function getLabInfo() {
  const uid = app.auth().currentUser!.uid;
  const labRef = getLabRef(uid);
  const labInfo = (await labRef.get()).data();
  return labInfo;
}

export function useLabCategory(categoryId: string) {
  const uid = app.auth().currentUser!.uid;
  return useQuery(`test-category-${categoryId}`, () =>
    getLabCategory(categoryId)
  );
}

export function useLabInfo() {
  const uid = app.auth().currentUser!.uid;
  return useQuery(`test-category-${uid}`, () => getLabInfo());
}

export function useCategoryDiscounts(categoryId: string) {
  return useQuery(`category-discounts-${categoryId}`, () =>
    getCategoryDiscounts(categoryId)
  );
}

export function getCategoryDiscounts(
  categoryId: string
): Promise<Array<Discount>> {
  const uid = app.auth().currentUser!.uid;
  return app
    .firestore()
    .collection("labs")
    .doc(uid)
    .collection("labTestCategories")
    .doc(categoryId)
    .collection("discounts")
    .where("endTime", ">", new Date().getTime())
    .orderBy("endTime")
    .get()
    .then((discounts) =>
      discounts.docs.map((i) => new Discount({ discountData: i.data() }))
    );
}

export function useCreateCategoryDiscount() {
  const queryCache = useQueryCache();
  return useMutation((discount: Discount) => createOrUpdateDiscount(discount), {
    onSuccess: (discount) => {
      queryCache.invalidateQueries(`category-discounts-${discount.categoryId}`);
    },
  });
}

export async function createOrUpdateDiscount(discount: Discount): Promise<any> {
  const uid = app.auth().currentUser!.uid;
  let discountRef = getCategoryDiscountRef({
    categoryId: discount.categoryId,
    discountId: discount.id,
  });
  if (!discount.id) {
    discount.labId = uid;
    discount.id = discountRef.id;
  }
  await discountRef.set({ ...getDiscountValues(discount) }, { merge: true });
  return discount;
}

function getCategoryDiscountRef({
  categoryId,
  discountId,
}: {
  categoryId: string;
  discountId?: string;
}) {
  const uid = app.auth().currentUser!.uid;
  if (!discountId) {
    discountId = undefined;
  }
  return app
    .firestore()
    .collection("labs")
    .doc(uid)
    .collection("labTestCategories")
    .doc(categoryId)
    .collection("discounts")
    .doc(discountId);
}

export function useDeleteCategoryDiscount() {
  const queryCache = useQueryCache();
  return useMutation((discount: Discount) => deleteDiscount(discount), {
    onSuccess: (discount: any) => {
      let testDiscounts: any = queryCache.getQueryData(
        `category-discounts-${discount.categoryId}`
      );

      testDiscounts = testDiscounts.filter(
        (dis: Discount) => dis.id !== discount.id
      );

      queryCache.setQueryData(
        `category-discounts-${discount.categoryId}`,
        testDiscounts
      );
    },
  });
}

export async function deleteDiscount(discount: Discount): Promise<any> {
  let discountRef = getCategoryDiscountRef({
    categoryId: discount.categoryId,
    discountId: discount.id,
  });

  await discountRef.delete();
  return discount;
}

export function useLabTestCategories() {
  return useQuery("lab-test-categories", getLabTestCategories);
}

export function getLabTestCategories() {
  const uid = app.auth().currentUser!.uid;
  return app
    .firestore()
    .collection("labs")
    .doc(uid)
    .collection("labTestCategories")
    .orderBy("createdAt", "desc")
    .get()
    .then((tests) => tests.docs.map((i) => i.data()));
}

export function useDeleteLabTestCategory() {
  const queryCache = useQueryCache();
  const uid = app.auth().currentUser!.uid;

  return useMutation(
    (categoryId) =>
      app
        .firestore()
        .collection("labs")
        .doc(uid)
        .collection("labTestCategories")
        .doc(categoryId)
        .delete(),
    {
      onSuccess: (_, categoryId) => {
        queryCache.setQueryData("lab-test-categories", (old: any) =>
          old.filter((i: any) => i.id !== categoryId)
        );
        queryCache.invalidateQueries("lab-test-categories");
      },
    }
  );
}

export function useAddLabTestCategory() {
  const queryCache = useQueryCache();
  const uid = app.auth().currentUser!.uid;
  return useMutation(
    ({ categoryData, initialDiscount }: any) =>
      addTestCategoryToLab({ categoryData, initialDiscount }),
    {
      onSuccess: () => {
        queryCache.invalidateQueries("lab-test-categories");
      },
    }
  );
}
export async function uploadFile(file: any, path: string) {
  if (file) {
    let ref = app.storage().ref().child(path);
    let uploadTaskSnap = await ref.put(file);
    let downloadUrl = await uploadTaskSnap.ref.getDownloadURL();
    return downloadUrl;
  }
}
export async function addTestCategoryToLab({
  categoryData,
  initialDiscount,
}: any) {
  const uid = app.auth().currentUser!.uid;
  const ref = app
    .firestore()
    .collection("labs")
    .doc(uid)
    .collection("labTestCategories")
    .doc();
  const id = ref.id;

  const file = categoryData.file;
  let iconUrl = "";
  if (file) {
    iconUrl = await uploadFile(file?.[0]?.img, `testCategories/${id}/icon.png`);
  }
  delete categoryData.file;
  await ref.set({
    ...categoryData,
    id,
    labId: uid,
    rank: 200,
    customPackage: true,
    createdAt: new Date().getTime(),
    iconUrl,
  });
  if (initialDiscount?.name) {
    createOrUpdateDiscount({ ...initialDiscount, labId: uid, categoryId: id });
  }
}

// migration for the testCategories to collection for current lab
export async function migrateTestCategoriesForAllLabs() {
  const allLabs = (await app.firestore().collection("labs").get()).docs.map(
    (lab) => lab.data()
  );
  const labsPromises = allLabs.map((lab) => migrateLabTestCategories(lab));
  await Promise.all(labsPromises);
}

async function migrateLabTestCategories(lab: any) {
  const testCategories = lab?.testCategories ?? [];
  console.log("Migratingg lab labId", lab?.name, lab?.id);

  for (let i = 0; i < testCategories.length; i++) {
    const category = testCategories[i];
    await createSubCollectionForTestCategories(category, lab?.id);
  }
}

async function createSubCollectionForTestCategories(
  category: string,
  labId: string
) {
  const categoryParentData = (await getParentCategoryData(category)) ?? {};
  const labTestCategoriesRef = app
    .firestore()
    .collection("labs")
    .doc(labId)
    .collection("labTestCategories")
    .doc(category);

  const categoryExits = (await labTestCategoriesRef.get()).exists;
  console.log("categoryExits: ", category, "xa xaina data", categoryExits);

  if (!categoryExits) {
    await labTestCategoriesRef.set({
      ...categoryParentData,
      rank: 100,
    });
  } else {
    await labTestCategoriesRef.update({
      ...categoryParentData,
      rank: 100,
    });
  }
  console.log("sub collection created success");
}

async function getParentCategoryData(catId: string) {
  const catData = (
    await app.firestore().collection("testCategories").doc(catId).get()
  ).data();
  return catData;
}

export function useEditLabTestCategory() {
  const queryCache = useQueryCache();

  return useMutation(
    (categoryData: any) => updateTestCategoryToLab(categoryData),
    {
      onSuccess: (res, variables) => {
        queryCache.setQueryData("lab-test-categories", (old: any) =>
          old.map((i: any) => {
            if (i.id === variables.id) {
              i.fee = variables.fee;
            }
            return i;
          })
        );
        queryCache.invalidateQueries("lab-test-categories");
      },
    }
  );
}

export async function updateTestCategoryToLab(categoryData: any) {
  const uid = app.auth().currentUser!.uid;
  const ref = getLabRef(uid)
    .collection("labTestCategories")
    .doc(categoryData.id);
  const file = categoryData.file;

  if (file) {
    categoryData.iconUrl = await uploadFile(
      file?.[0]?.img,
      `testCategories/${categoryData.id}/icon.png`
    );
  }
  delete categoryData.file;
  return ref.update({ ...categoryData, updatedAt: new Date().getTime() });
}
